"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { FBPIXPush } from "@/services/fbConversionService";
import { axiosGetWishlist, axiosGetOverallProductRatings } from "@/utilities/axioscall";
import { APPENUMS } from "@/constants";
import { motion, Variants, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { addtowishlist } from "@/clicks/wishlistClicks";
import { useUser } from "@/providers/userContext";
import { getAssetsBaseUrl, getCurrencySymbol } from "@/services/storeService";

interface Product {
  id: number;
  name: string;
  seo_url: string;
  imageurl?: string;
  sp: number;
  mrp: number;
  category_id?: number;
  category_parent_id?: number;
  sku?: string;
  eng_name?: string;
  visibility?: number;
}

interface Category {
  id: number;
  name: string;
  parent_id?: number;
}

interface MyWishlistProps {
  productsListData: Product[];
  categoryListData: Category[];
}

export default function MyWishlist({ productsListData, categoryListData }: MyWishlistProps) {
  const assets = `${getAssetsBaseUrl()}`
  const router = useRouter();

  const { user, isLoggedIn } = useUser();

  const [pageInit, setPageInit] = useState(false);
  const [productWishlistData, setProductWishlistData] = useState<string[]>([]);
  const [productsListPageData, setProductsListPageData] = useState<Product[]>([]);
  const [productsListDisplayData, setProductsListDisplayData] = useState<Product[]>([]);
  const [productOverallRatingData, setProductOverallRatingData] = useState<
    { product_id: number; average_rating: string }[]
  >([]);
  const [productFetchWLLoading, setProductFetchWLLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHeartAnimating, setIsHeartAnimating] = useState<{ [key: string]: boolean }>({});

  const fetchWishlistData = async () => {
    setProductFetchWLLoading(true);
    const wishlistData = await axiosGetWishlist(setProductWishlistData);
    await axiosGetOverallProductRatings(setProductOverallRatingData);
    setProductFetchWLLoading(false);
    filterProducts(null, wishlistData);
  };

  const filterProducts = (query: string | null = null, wishlistData: string[] | null = null, categoryId: number | null = selectedCategory) => {
    let filteredProducts = productsListData;

    // Filter by wishlist
    filteredProducts = filteredProducts.filter((prod) =>
      (wishlistData || productWishlistData).includes(prod.id.toString())
    );

    // Filter by category only if categoryId is not null or 0
    if (categoryId !== null && categoryId !== 0) {
      filteredProducts = filteredProducts.filter(
        (prod) => prod.category_id === categoryId || prod.category_parent_id === categoryId
      );
    }

    // Filter by search query
    if (query) {
      const lowercaseQuery = query.toLowerCase();
      filteredProducts = filteredProducts.filter(
        (prod) =>
          prod.name.toLowerCase().includes(lowercaseQuery) ||
          (prod.sku && prod.sku.toLowerCase().includes(lowercaseQuery)) ||
          (prod.eng_name && prod.eng_name.toLowerCase().includes(lowercaseQuery))
      );
    }

    setProductsListPageData(filteredProducts);
    setProductsListDisplayData(filteredProducts.slice(0, displayCount));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterProducts(query);
  };

  const handleCategoryChange = (categoryId: number | null) => {
    setSelectedCategory(categoryId);
    setIsDropdownOpen(false);
    filterProducts(searchQuery, null, categoryId);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory(null);
    filterProducts(null, productWishlistData);
  };

  const viewProductInfo = (product: Product) => {
    router.push(`/p/${product.seo_url}`);
  };

  const handleWishlistToggle = async (product: Product) => {
    setIsHeartAnimating((prev) => ({ ...prev, [product.id]: true }));

    try {
      // Call API to toggle wishlist
      const response = await addtowishlist(product, productWishlistData, setProductWishlistData, router.push);

      const isInWishlist = productWishlistData.includes(product.id.toString());
      const updatedWishlist = isInWishlist
        ? productWishlistData.filter((id) => id !== product.id.toString())
        : [...productWishlistData, product.id.toString()];

      setProductWishlistData(updatedWishlist);
      filterProducts(searchQuery, updatedWishlist);

    } catch (error) {
      console.log("responseresponse")
    } finally {
      setTimeout(() => {
        setIsHeartAnimating((prev) => ({ ...prev, [product.id]: false }));
      }, 500);
    }
  };

  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    if (!isLoggedIn) {
      router.push("/signin?from=my-wishlist");
      notify("Log in to see your wishlist.");
      return;
    }

    if (productsListData.length > 0) {
      setPageInit(true);
      FBPIXPush(APPENUMS.EVENT_ENUMS.MY_WISHLIST);
      fetchWishlistData();
    }
  }, [isLoggedIn, productsListData]);

  const notify = (msg: any) =>
    toast.success(msg, {
      style: {
        border: "1px solid #00b852",
        padding: "16px",
        color: "#00b852",
        zIndex: "10000 !important",
      },
      iconTheme: {
        primary: "#00b852",
        secondary: "#FFFAEE",
      },
      duration: 2000,
    });

  // Animation variants
  const inputVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const buttonVariants: Variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
  };

  const dropdownVariants: Variants = {
    hidden: { opacity: 0, y: -10, height: 0 },
    visible: { opacity: 1, y: 0, height: "auto", transition: { duration: 0.3, ease: "easeOut" } },
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.3 } },
  };

  const mainCategories = categoryListData.filter(
    (cat) => cat.parent_id === null || cat.parent_id === 0
  );

  return (
    <section className="container mx-auto py-4 md:py-6 lg:py-8">
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
        <h1 className="text-2xl md:text-3xl font-bold mb-4">My Wishlist</h1>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 mb-6"
          initial="hidden"
          animate="visible"
          variants={inputVariants}
        >
          <input
            type="text"
            placeholder="Search by name, SKU, or English name..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full sm:w-1/2 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="relative w-full sm:w-1/4">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full p-2 border rounded-md bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-primary hover:bg-gray-50"
            >
              <span className="text-gray-700">
                {selectedCategory
                  ? categoryListData.find((cat) => cat.id === selectedCategory)?.name || "All Categories"
                  : "All Categories"}
              </span>
              <ChevronDown className="w-5 h-5 text-gray-500" />
            </button>
            {isDropdownOpen && (
              <motion.div
                className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto"
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
              >
                <div
                  className="p-2 hover:bg-blue-100 cursor-pointer"
                  onClick={() => handleCategoryChange(null)}
                >
                  All Categories
                </div>
                {mainCategories.map((category) => (
                  <div
                    key={category.id}
                    className="p-2 hover:bg-blue-100 cursor-pointer"
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    {category.name}
                  </div>
                ))}
              </motion.div>
            )}
          </div>
          <motion.div variants={buttonVariants}>
            <button
              onClick={handleClearFilters}
              className="w-full sm:w-auto p-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              Clear Filters
            </button>
          </motion.div>
        </motion.div>

        {productFetchWLLoading ? (
          <div className="flex justify-center items-center py-10">
            <img src="/ecomsaas-assets/loader/tshirt.gif" alt="Loading..." className="w-32" />
          </div>
        ) : productsListDisplayData.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <AnimatePresence>
              {productsListDisplayData.map((product) => {
                const imageUrl = product.imageurl?.split(",")[0]?.trim();
                const finalImage = imageUrl
                  ? `${assets}/${imageUrl}`
                  : "/ecomsaas-assets/images/no-image.webp";
                const ratingData = productOverallRatingData.find(
                  (prod) => prod.product_id === product.id
                );
                const avgRating = ratingData ? parseFloat(ratingData.average_rating) : 0;

                return (
                  <motion.div
                    key={product.id}
                    className="group bg-white rounded-lg shadow-sm p-3"
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <div
                      className="relative overflow-hidden w-full pt-[100%] cursor-pointer"
                      onClick={() => viewProductInfo(product)}
                    >
                      <img
                        src={finalImage}
                        alt={product.name}
                        className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = "/ecomsaas-assets/images/no-image.webp";
                        }}
                        loading="lazy"
                        decoding="async"
                      />
                      {avgRating > 0 && (
                        <div className="absolute bottom-2 left-2 flex items-center bg-white px-1.5 py-0.5 rounded">
                          <svg
                            className="w-4 h-4 text-yellow-500 fill-yellow-500"
                            viewBox="0 0 24 24"
                          >
                            <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                          </svg>
                          <span className="text-xs ml-1">{avgRating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <div className="flex justify-between items-start">
                        <h3
                          className="text-sm w-[80%] md:text-base font-medium line-clamp-2 cursor-pointer"
                          onClick={() => viewProductInfo(product)}
                        >
                          {product.name}
                        </h3>
                        <svg
                          onClick={() => handleWishlistToggle(product)}
                          className={`w-5 h-5 md:w-6 md:h-6 z-40 cursor-pointer ${isHeartAnimating[product.id] ? "animate-heart" : ""
                            }`}
                          viewBox="0 0 27 27"
                          fill={productWishlistData.includes(product.id.toString()) ? "#f45440" : "none"}
                          stroke={productWishlistData.includes(product.id.toString()) ? "#f45440" : "black"}
                          strokeWidth="1.5"
                        >
                          <path d="M13.4993 26.2061L4.70067 16.9253C3.9281 16.1443 3.41815 15.1374 3.24307 14.0471C3.06798 12.9568 3.23664 11.8385 3.72514 10.8505V10.8505C4.09415 10.1046 4.63318 9.45803 5.29779 8.96406C5.96241 8.47008 6.73359 8.14284 7.54782 8.00931C8.36204 7.87578 9.19599 7.93978 9.98095 8.19603C10.7659 8.45228 11.4794 8.89345 12.0627 9.48319L13.4993 10.9358L14.9359 9.48319C15.5192 8.89345 16.2327 8.45228 17.0177 8.19603C17.8026 7.93978 18.6366 7.87578 19.4508 8.00931C20.265 8.14284 21.0362 8.47008 21.7008 8.96406C22.3654 9.45803 22.9045 10.1046 23.2735 10.8505V10.8505C23.762 11.8385 23.9306 12.9568 23.7556 14.0471C23.5805 15.1374 23.0705 16.1443 22.298 16.9253L13.4993 26.2061Z" />
                        </svg>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm font-semibold">{getCurrencySymbol()} {product.sp}</p>
                        <p className="text-xs text-gray-500 line-through">{getCurrencySymbol()} {product.mrp}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : productWishlistData.length > 0 ? (
          <div className="text-center py-10">
            <img
              src="/ecomsaas-assets/images/no-image.webp"
              alt="No Items"
              className="w-32 mx-auto"
            />
            <h3 className="text-lg font-semibold mt-4">No Items Match Your Filters</h3>

            <p className="text-gray-600 mt-2">
              Or click <a href="/products" className="text-primary">here</a> to continue shopping.
            </p>
          </div>
        ) : (
          <div className="text-center py-10">
            <img
              src="/ecomsaas-assets/loader/nodata-founds.png"
              alt="Empty Wishlist"
              className="w-32 mx-auto"
            />
            <h3 className="text-lg font-semibold mt-4">Wishlist is empty.</h3>
            <p className="text-gray-600">
              Looks like you have no items in your wishlist. <br />
              Click <a href="/products" className="text-blue-700 underline">here</a> to continue shopping.
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes heartAnimation {
          0% {
            transform: scale(1);
          }
          20% {
            transform: scale(1.3);
          }
          40% {
            transform: scale(1.1) rotate(-5deg);
          }
          60% {
            transform: scale(1.2) rotate(5deg);
          }
          80% {
            transform: scale(1.1) rotate(-3deg);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-heart {
          animation: heartAnimation 0.5s ease-in-out;
        }
      `}</style>
    </section>
  );
}

